import { useTheme } from '../../../../theme';
import { styled } from '@mui/material/styles';
import { type StyledComponent } from '@emotion/styled';
import { type HTMLAttributes, type DetailedHTMLProps } from 'react';

type StyledIcon = StyledComponent<
  object,
  DetailedHTMLProps<HTMLAttributes<HTMLSpanElement>, HTMLSpanElement>,
  object
>;

const RadioBase = styled('span')(() => ({
  '&:before': {
    position: 'absolute',
    height: 20,
    width: 20,
    left: 0,
    top: 0,
    content: '""',
  },
}));

const createSvgUrl = (svgContent: string): string =>
  `url('data:image/svg+xml,${encodeURIComponent(svgContent)}')`;

export const RadioChecked: StyledIcon = styled(RadioBase)(() => {
  const theme = useTheme();
  return {
    '&:before': {
      backgroundImage: createSvgUrl(`
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M2 10C2 5.58172 5.58172 2 10 2C14.4183 2 18 5.58172 18 10C18 14.4183 14.4183 18 10 18C5.58172 18 2 14.4183 2 10Z" fill="${theme.radioButton.active.default}"/>
          <circle cx="10" cy="10" r="4" fill="${theme.radioButton.selector.default}" />
        </svg>
      `),
    },

    'input:hover ~ &': {
      '&:before': {
        backgroundImage: createSvgUrl(`
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="2" y="2" width="16" height="16" rx="8" fill="${theme.radioButton.active.hover}"/>
            <circle cx="10" cy="10" r="4" fill="${theme.radioButton.selector.default}"/>
          </svg>
        `),
      },
    },

    'input:active ~ &': {
      '&:before': {
        backgroundImage: createSvgUrl(`
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="2" y="2" width="16" height="16" rx="8" fill="${theme.radioButton.default.pressed}"/>
            <circle cx="10" cy="10" r="4" fill="${theme.radioButton.selector.default}"/>
          </svg>
        `),
      },
    },

    'input:focus ~ &': {
      '&:before': {
        backgroundImage: createSvgUrl(`
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <rect x="2" y="2" width="16" height="16" rx="8" fill="${theme.radioButton.active.default}"/>
            <circle cx="10" cy="10" r="8" fill="${theme.radioButton.active.default}"/>
            <circle cx="10" cy="10" r="9" stroke="${theme.radioButton.active.default}" stroke-opacity="0.3" stroke-width="2"/>
            <circle cx="10" cy="10" r="4" fill="${theme.radioButton.selector.default}"/>
          </svg>
        `),
      },
    },

    'input:disabled ~ &': {
      '&:before': {
        backgroundImage: createSvgUrl(`
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="2" y="2" width="16" height="16" rx="8" fill="${theme.radioButton.active.disabled}" />
            <circle cx="10" cy="10" r="4" fill="${theme.radioButton.active.disabled}" />
            <circle cx="10" cy="10" r="4" fill="${theme.radioButton.selector.disabled}"/>
          </svg>
        `),
      },
    },
  };
});

export const RadioUnchecked: StyledIcon = styled(RadioBase)(() => {
  const theme = useTheme();
  return {
    '&:before': {
      backgroundImage: createSvgUrl(`
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="2.5" y="2.5" width="15" height="15" rx="7.5" fill="${theme.radioButton.background}"/>
          <rect x="2.5" y="2.5" width="15" height="15" rx="7.5" stroke="${theme.radioButton.default.default}"/>
        </svg>
      `),
    },

    'input:hover ~ &': {
      '&:before': {
        backgroundImage: createSvgUrl(`
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="2.5" y="2.5" width="15" height="15" rx="7.5" fill="${theme.radioButton.background}"/>
            <rect x="2.5" y="2.5" width="15" height="15" rx="7.5" stroke="${theme.radioButton.default.hover}"/>
          </svg>
        `),
      },
    },

    'input:active ~ &': {
      '&:before': {
        backgroundImage: createSvgUrl(`
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="2.5" y="2.5" width="15" height="15" rx="7.5" fill="white"/>
            <rect x="2.5" y="2.5" width="15" height="15" rx="7.5" stroke="${theme.radioButton.default.pressed}"/>
          </svg>
        `),
      },
    },

    'input:focus ~ &': {
      '&:before': {
        backgroundImage: createSvgUrl(`
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clip-path="url(#clip0_17925_50751)">
              <rect x="2.5" y="2.5" width="15" height="15" rx="7.5" fill="${theme.radioButton.background}"/>
              <rect x="2.5" y="2.5" width="15" height="15" rx="7.5" stroke="${theme.radioButton.default.default}"/>
              <circle cx="10" cy="10" r="9" stroke="${theme.border.interactive.defaultFocused}" stroke-width="2"/>
            </g>
            <defs>
              <clipPath id="clip0_17925_50751">
                <rect width="20" height="20" fill="${theme.radioButton.background}"/>
              </clipPath>
            </defs>
          </svg>
        `),
      },
    },

    'input:disabled ~ &': {
      '&:before': {
        backgroundImage: createSvgUrl(`
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="2.5" y="2.5" width="15" height="15" rx="7.5" fill="${theme.radioButton.background}"/>
            <rect x="2.5" y="2.5" width="15" height="15" rx="7.5" stroke="${theme.radioButton.default.disabled}"/>
          </svg>
          `),
      },
    },
  };
});
